import React, {useState} from "react";
import i18next from "i18next";
import {Button, Spin} from "antd";
import {ArrowLeftOutlined, CheckCircleFilled, CopyOutlined} from "@ant-design/icons";
import copy from "copy-to-clipboard";
import * as Setting from "../../../Setting";
import {EmailMfaType, SmsMfaType, TotpMfaType} from "../../MfaSetupPage";
import {requestMfaEnable} from "../../mfa/MfaEnableForm";

// the code is sent on load to the user's email or phone, when they have one (MfaVerifySmsForm)
function getVerifySubtitle(mfaType, user) {
  switch (mfaType) {
  case EmailMfaType:
    return user.email ? i18next.t("mfa:We'll send a code to your email to confirm it's yours") : null;
  case SmsMfaType:
    return user.phone ? i18next.t("mfa:We'll send a code to your phone to confirm it's yours") : null;
  case TotpMfaType:
    return i18next.t("mfa:Scan the QR code with your authenticator app, then enter the code it shows");
  default:
    return i18next.t("mfa:Each time you sign in to your Account, you'll need your password and a authentication code");
  }
}

// MfaSetupPage's steps; the password step is skipped when the sign-in sent the user here
function getSteps(page) {
  const steps = [
    {title: i18next.t("general:Password"), subtitle: i18next.t("mfa:Confirm your password to continue")},
    {title: i18next.t("forget:Verify"), subtitle: getVerifySubtitle(page.state.mfaType, page.getUser())},
    {title: i18next.t("general:Enable"), subtitle: i18next.t("mfa:Save this recovery code somewhere safe. You can use it to sign in if you lose access to your device")},
  ];
  return page.state.isPromptPage ? steps.slice(1) : steps;
}

// The last step: the recovery code, ready to copy, and the button that turns MFA on. Instead of
// MfaEnableForm, so the page can show that MFA is on while it takes the user back.
function AvironMfaEnableStep({page}) {
  const [loading, setLoading] = useState(false);
  const {mfaProps, mfaType, dest, countryCode} = page.state;
  const recoveryCode = mfaProps.recoveryCodes[0];

  const enable = () => {
    setLoading(true);
    requestMfaEnable({user: page.getUser(), mfaType, secret: mfaProps.secret, recoveryCodes: mfaProps.recoveryCodes, dest, countryCode})
      .then((res) => {
        if (res.status === "ok") {
          page.setState({mfaEnabled: true});
          page.onMfaEnabled();
        } else {
          setLoading(false);
          Setting.showMessage("error", `${i18next.t("general:Failed to enable")}: ${res.msg}`);
        }
      })
      .catch((error) => {
        setLoading(false);
        Setting.showMessage("error", `${i18next.t("general:Failed to connect to server")}: ${error}`);
      });
  };

  return (
    <div className="aviron-mfa-enable">
      <div className="aviron-recovery-header">
        <span className="aviron-mfa-label">{i18next.t("mfa:Recovery code")}</span>
        <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => {
          copy(recoveryCode);
          Setting.showMessage("success", i18next.t("general:Copied to clipboard successfully"));
        }}>
          {i18next.t("general:Copy")}
        </Button>
      </div>
      <code className="aviron-recovery-code">{recoveryCode}</code>
      <Button type="primary" block className="login-button" loading={loading} onClick={enable}>
        {i18next.t("general:Enable")}
      </Button>
    </div>
  );
}

function renderEnabled() {
  return (
    <div className="aviron-reset-done" role="status">
      <CheckCircleFilled className="aviron-reset-done-icon" aria-hidden="true" />
      <h1 className="aviron-title">{i18next.t("mfa:All set")}</h1>
      <p className="aviron-subtitle">{i18next.t("mfa:Two-step verification is on. Taking you back")}</p>
      <Spin />
    </div>
  );
}

function renderStep(page) {
  const {current, mfaProps, applicationError} = page.state;
  if (current === 2) {
    return <AvironMfaEnableStep page={page} />;
  }
  if (current === 1 && mfaProps === null && !applicationError) {
    return <div className="aviron-mfa-loading"><Spin /></div>;
  }
  return page.renderStep();
}

// The Aviron shell of the MFA setup page, in the login card: the logo, the title, a progress bar and
// MfaSetupPage's own forms. `page` is the ThemedMfaSetupPage instance, which keeps MfaSetupPage's
// state and logic.
export function renderAvironMfaSetupPage(page, application) {
  const logo = (
    <div className="login-logo-box">
      {Setting.renderLogo(application)}
    </div>
  );

  let content;
  if (page.state.mfaEnabled) {
    content = <React.Fragment>{logo}{renderEnabled()}</React.Fragment>;
  } else {
    const steps = getSteps(page);
    const current = page.state.isPromptPage ? page.state.current - 1 : page.state.current;
    content = (
      <React.Fragment>
        {
          // signing in, the user can only go on; from their account, they can go back to it
          page.state.isPromptPage ? null : (
            <Button type="text" className="aviron-back-button" icon={<ArrowLeftOutlined />} onClick={() => page.props.history.push("/account")}>
              {i18next.t("general:Back")}
            </Button>
          )
        }
        {logo}
        <div className="aviron-heading">
          <h1 className="aviron-title">{i18next.t("mfa:Secure your account")}</h1>
          {steps[current]?.subtitle ? <p className="aviron-subtitle">{steps[current].subtitle}</p> : null}
        </div>
        <ol className="aviron-steps" style={{gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`}}>
          {
            steps.map((step, index) => (
              <li key={step.title} className={index < current ? "is-done" : index === current ? "is-current" : null} aria-current={index === current ? "step" : undefined}>
                <span className="aviron-steps-bar" aria-hidden="true" />
                <span className="aviron-steps-label">{step.title}</span>
              </li>
            ))
          }
        </ol>
        {renderStep(page)}
      </React.Fragment>
    );
  }

  return (
    <div className="login-content aviron-forget aviron-mfa-setup">
      <div className="login-panel">
        <div className="login-form">
          {Setting.renderHelmet(application)}
          {content}
        </div>
      </div>
    </div>
  );
}
